import { useEffect } from 'react';
import { ConsumerToggleResponse } from '../types/ConsumerToggle';
import { getSupportedBrowserDefaultConsumerLocale } from '../util/BrowserLanguageUtil';
import { useAuth0 } from '@auth0/auth0-react';
import { useAuthData } from '../LoginContext';
import { useTranslationData } from '../TranslationContext';
import { useFirebaseText } from './useFirebaseText';
import { handleApiError } from '../util/ErrorHandlerUtil';

/**
 * Custom hook that handles the application's initialization process.
 * This includes authentication verification, consumer selection, language setup,
 * and initial text translation preparation.
 */
export const useAppInitialization = (clientId: string | undefined) => {
	const { isAuthenticated, logout } = useAuth0();
	const {
		auth0IdToken,
		selectedConsumer,
		isAppInitialized,
		setSelectedConsumerId,
		setSelectedConsumer,
		setIsAppInitialized,
		initConsumerToggleOptions
	} = useAuthData();

	const { languageTo, initLanguages, setLanguageTo } = useTranslationData();

	useFirebaseText();

	/**
	 * Determines and sets the current consumer based on the toggle response
	 */
	const getCurrentConsumer = (consumerToggleResponse: ConsumerToggleResponse | null) => {
		const currentConsumer = consumerToggleResponse?.consumerToggleOptions.find(i => i.name === selectedConsumer);

		let consumerId = null;
		let consumerName = null;
		if (currentConsumer) {
			consumerId = currentConsumer.id;
			consumerName = currentConsumer.name;
		} else {
			consumerId = consumerToggleResponse?.selectedConsumerId ?? null;
			consumerName = consumerToggleResponse?.selectedConsumer ?? null;
		}
		setSelectedConsumerId(consumerId);
		setSelectedConsumer(consumerName);
		return consumerId;
	};

	useEffect(() => {
		/**
		 * Initializes the application data in the following order:
		 * 1. Fetches and sets consumer toggle options
		 * 2. Determines current consumer
		 * 3. Initializes supported languages
		 * 4. Sets default language if needed
		 */
		const initializeAppData = async () => {
			try {
				const consumerToggleResponse = await initConsumerToggleOptions(auth0IdToken);
				if (consumerToggleResponse) {
					const consumerId = getCurrentConsumer(consumerToggleResponse);
					const languages = await initLanguages(auth0IdToken, consumerId);

					if (languageTo === '') {
						const defaultConsumerLocale = getSupportedBrowserDefaultConsumerLocale(languages);
						setLanguageTo(defaultConsumerLocale);
					}
				}
				setIsAppInitialized(true);
			} catch (e) {
				setIsAppInitialized(true);
				const {shouldLogout, message} = handleApiError(e)
				if (shouldLogout) {
					localStorage.setItem('login-error-message', message);
					logout({
						clientId: clientId,
						logoutParams: {
							returnTo: window.location.origin
						}
					});
				}
			}
		};

		if (isAuthenticated && auth0IdToken !== null && !isAppInitialized) {
			initializeAppData();
		}
	}, [auth0IdToken, isAuthenticated, isAppInitialized]); // Only initialize if user is authenticated, has a token, and app isn't already initialized


	return { isAppInitialized };
};